// ProcessCodLead.js
import React, { useEffect, useState, useMemo, useRef } from "react"
import { Link, useParams } from "react-router-dom"
import axios from "axios"
import toastr from "toastr"
import "toastr/build/toastr.min.css"
import apiURl from "url"
import { Container, UncontrolledTooltip } from "reactstrap"
import { useReactToPrint } from "react-to-print"
import PrintCodLead from "./PrintSubmitCodLead"

export const formatCurrency = (value, currency = "PKR", options = {}) => {
  if (value === null || value === undefined || isNaN(Number(value))) return "-"
  return new Intl.NumberFormat("en-PK", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    ...options,
  }).format(Number(value))
}

export const formatDateTime = (value, options = {}) => {
  if (!value) return "-"
  const d = new Date(value)
  if (isNaN(d.getTime())) return "-"
  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Asia/Karachi",
    ...options,
  }).format(d)
}

const ProcessLeadPage = () => {
  const { id } = useParams()
  const resolvedLeadId = id

  const [loading, setLoading] = useState(true)
  const [lead, setLead] = useState(null)

  const [riderOptions, setRiderOptions] = useState([])
  const [selectedRider, setSelectedRider] = useState("")
  const [zone, setZone] = useState("")
  const [editMode, setEditMode] = useState(false)
  const [charges, setCharges] = useState(null)
  const [fallbackCharges, setFallbackCharges] = useState(null)
  const [isPrinting, setIsPrinting] = useState(false)

  const fetchCharges = async id => {
    try {
      const r = await axios.get(`${apiURl}/cod-leads-final/charges/${id}`)
      if (r.data?.status === "success" && r.data.data) {
        setCharges(r.data.data)
        return r.data.data
      }
      setCharges(null)
      return null
    } catch {
      setCharges(null)
      return null
    }
  }

  const printRef = useRef(null)
  // tiny helper to wait for React to paint after state updates
  const waitNextFrame = () =>
    new Promise(resolve =>
      requestAnimationFrame(() => requestAnimationFrame(resolve))
    )

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `COD_${resolvedLeadId}`,
    onBeforeGetContent: async () => {
      // Only fetch charges if the lead is already persisted (has zone + rider)
      if (lead?.zone && lead?.assigned_rider_id) {
        if (!charges) {
          await fetchCharges(resolvedLeadId)
        }
      } else {
        setCharges(null)
      }
      // ensure DOM updates are flushed before printing
      await waitNextFrame()
    },
    onAfterPrint: () => setIsPrinting(false),
    removeAfterPrint: true,
  })

  const onClickPrint = async () => {
    if (!lead) return
    setIsPrinting(true)
    handlePrint() // onBeforeGetContent will fetch charges & wait for DOM
  }
  useEffect(() => {
    if (!isPrinting) return
    const t = setTimeout(() => setIsPrinting(false), 10000)
    return () => clearTimeout(t)
  }, [isPrinting])

  // --- fetch one lead (matches new API shape) ---
  useEffect(() => {
    if (!resolvedLeadId) return
    ;(async () => {
      try {
        setLoading(true)
        const res = await axios.get(
          `${apiURl}/single-cod-full-data/${resolvedLeadId}`
        )
        if (res.data?.status === "success" && res.data.data) {
          const raw = res.data.data

          // derive status from the latest history row if present
          const latestStatus = raw.history?.[0]?.status || null

          const normalized = {
            ...raw,
            // keep your FE expectations for created/updated aliases
            lead_created_at: raw.lead_createdAt,
            lead_updated_at: raw.lead_updatedAt,
            // maintain a lead_status field for your existing UI logic
            lead_status: latestStatus,
          }

          setLead(normalized)
          setZone(normalized.zone ?? "")
          setSelectedRider(normalized.assigned_rider_id ?? "")
          setEditMode(false)
        } else {
          toastr.error(res.data?.message || "Lead not found")
        }
      } catch {
        toastr.error("Failed to load lead")
      } finally {
        setLoading(false)
      }
    })()
  }, [resolvedLeadId])

  // after existing state
  const [quote, setQuote] = useState(null)
  const [quoteLoading, setQuoteLoading] = useState(false)
  const [quoteError, setQuoteError] = useState("")

  //Final Rates
  useEffect(() => {
    const run = async () => {
      setQuote(null)
      setQuoteError("")
      if (!zone) return

      try {
        setQuoteLoading(true)
        const res = await axios.post(`${apiURl}/quote-cod-lead`, {
          lead_id: resolvedLeadId,
          zone, // within_city | same_zone | different_zone
        })
        if (res.data?.status === "success") {
          setQuote(res.data.data)
        } else {
          setQuoteError(res.data?.message || "Failed to compute quote")
        }
      } catch (e) {
        setQuoteError("Failed to compute quote")
      } finally {
        setQuoteLoading(false)
      }
    }
    run()
  }, [zone, resolvedLeadId])

  const originAddress = lead?.origin || lead?.business?.city || "-"
  const originalBusinessId = lead?.business?.id
  const dropAddress =
    lead?.consignee?.full_address ||
    lead?.consignee_full_address ||
    lead?.consignee?.city ||
    lead?.consignee_city ||
    "-"
  const DropCity = lead?.consignee?.city

  // --- fetch riders (unchanged) ---
  useEffect(() => {
    ;(async () => {
      try {
        const URL = `${apiURl}/getApprovedRiders`
        const response = await axios.get(URL)
        if (response.data.status === "success") {
          const options = response.data.riders.map(r => ({
            label: r.name,
            value: r.id,
          }))
          setRiderOptions(options)
        } else {
          console.error("Failed to fetch riders:", response.data.message)
        }
      } catch (error) {
        console.error("Error fetching riders:", error)
      }
    })()
  }, [])

  // cancelled helper
  const isCancelled = useMemo(() => {
    const s = String(lead?.lead_status ?? "").toLowerCase()
    return s === "cancelled" || s === "canceled"
  }, [lead])

  // lock only if values exist in DB (persisted)
  const isPersistedLocked = useMemo(() => {
    return Boolean(lead?.zone && lead?.assigned_rider_id)
  }, [lead])

  // dirty when UI differs from DB values
  const isDirty = useMemo(() => {
    const persistedZone = lead?.zone ?? ""
    const persistedRider = String(lead?.assigned_rider_id ?? "")
    return zone !== persistedZone || String(selectedRider) !== persistedRider
  }, [lead, zone, selectedRider])

  // show matching (live) when zone picked AND (editing OR not persisted yet)
  const showMatchingCard = useMemo(
    () => Boolean(zone && (editMode || !isPersistedLocked)),
    [zone, editMode, isPersistedLocked]
  )

  // show stored (DB) when zone picked AND persisted AND not editing
  const showStoredCard = useMemo(
    () => Boolean(zone && isPersistedLocked && !editMode),
    [zone, isPersistedLocked, editMode]
  )

  // when viewing a persisted lead (not editing), ensure DB charges are loaded
  useEffect(() => {
    if (showStoredCard && resolvedLeadId) {
      fetchCharges(resolvedLeadId)
    }
  }, [showStoredCard, resolvedLeadId])

  if (loading) return <div className="p-3">Loading…</div>
  if (!lead) return <div className="p-3">Lead not found.</div>

  const business = lead.business || {}
  const consignee = lead.consignee || {}
  const creator = lead.creator || {}

  //print on submit
  const sleep = ms => new Promise(r => setTimeout(r, ms))

  // Try up to 5 times with a short delay until charges row exists
  const fetchChargesWithRetry = async (id, tries = 8, delayMs = 250) => {
    for (let i = 0; i < tries; i++) {
      try {
        const r = await axios.get(`${apiURl}/cod-leads-final/charges/${id}`, {
          headers: { "Cache-Control": "no-store" },
        })
        if (r.data?.status === "success" && r.data.data) {
          setCharges(r.data.data)
          return r.data.data
        }
      } catch {
        /* ignore and retry */
      }
      await sleep(delayMs)
    }
    return null
  }

  // Submit Request
  const submitRequist = async () => {
    if (!selectedRider) return toastr.warning("Select a rider first")
    if (!zone) return toastr.warning("Select a zone first")
    if (!quote) return toastr.warning("Quote not ready yet")

    try {
      if (isPersistedLocked && !editMode) {
        return toastr.info("Click Edit to make changes")
      }

      const endpoint = isPersistedLocked
        ? "/cod-leads-final/update"
        : "/cod-leads-final/finalize"

      const res = await axios.post(`${apiURl}${endpoint}`, {
        leadId: resolvedLeadId,
        assignedRiderId: selectedRider,
        zone,
        quote: {
          inputs: quote.inputs,
          matched: quote.matched,
          breakdown: quote.breakdown,
          total: quote.breakdown?.total ?? null,
        },
      })

      if (res.data?.status === "success") {
        toastr.success(isPersistedLocked ? "Lead updated" : "Lead finalized")
        setIsPrinting(true)
        // 1) local UI patch (unchanged)
        setLead(prev =>
          prev
            ? {
                ...prev,
                assigned_rider_id: selectedRider,
                zone,
                lead_status: "PRICED_AND_ASSIGNED",
                history: [
                  {
                    id: res.data.data.history_id,
                    cod_lead_id: Number(resolvedLeadId),
                    cod_lead_uni_id: prev.cod_lead_uni_id,
                    business_id: prev.business_id,
                    user_id: prev.user_id,
                    order_id: prev.order_id,
                    consignee_id: prev.consignee_id,
                    status: isPersistedLocked
                      ? "PRICED_AND_ASSIGNED_UPDATED"
                      : "PRICED_AND_ASSIGNED",
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                  },
                  ...(Array.isArray(prev.history) ? prev.history : []),
                ],
              }
            : prev
        )

        // 2) prime charges from the quote we just submitted (instant UI)
        const bd = quote?.breakdown || {}
        const seeded = {
          zone,
          base_weight_charge: Number(bd.base_weight_charge ?? 0),
          cash_handling: Number(bd.cash_handling ?? 0),
          fsc_amount: Number(bd.fsc_amount ?? 0),
          gst_amount: Number(bd.gst_amount ?? 0),
          total: Number(bd.total ?? 0),
        }
        setFallbackCharges(seeded)

        setEditMode(false)

        try {
          await fetchChargesWithRetry(resolvedLeadId) // sets `charges` when row is ready
          await waitNextFrame() // paint updated DOM
          handlePrint() // onAfterPrint will setIsPrinting(false)
        } catch (e) {
          setIsPrinting(false) // ensure overlay goes away on unexpected errors
        }
      } else {
        toastr.error(res.data?.message || "Failed to finalize lead")
      }
    } catch (e) {
      toastr.error("Failed to finalize lead")
    }
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div className="container py-3">
            {/* Print progress overlay */}
            {isPrinting && (
              <div
                role="dialog"
                aria-modal="true"
                aria-label="Preparing print"
                style={{
                  position: "fixed",
                  inset: 0,
                  background: "rgba(17, 24, 39, 0.45)", // slate-900/45
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 9999,
                }}
              >
                <div
                  style={{
                    background: "#fff",
                    borderRadius: 12,
                    padding: "18px 20px",
                    minWidth: 260,
                    boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
                    textAlign: "center",
                  }}
                >
                  <div
                    className="spinner-border text-primary"
                    role="status"
                    aria-hidden="true"
                  />
                  <div style={{ fontWeight: 700, marginTop: 10 }}>
                    Preparing print…
                  </div>
                  <div style={{ color: "#6b7280", fontSize: 12, marginTop: 4 }}>
                    Fetching charges & rendering sheet
                  </div>
                </div>
              </div>
            )}

            <div style={{ display: "none" }}>
              <div ref={printRef}>
                <PrintCodLead
                  id={resolvedLeadId}
                  lead={lead}
                  charges={charges}
                  fallbackCharges={fallbackCharges}
                />
              </div>
            </div>
            {/* Header */}
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h4 className="mb-0">
                Process Lead{" "}
                {lead.cod_lead_uni_id
                  ? `#${lead.cod_lead_uni_id}`
                  : `#${lead.id}`}{" "}
                <span
                  className={`badge ${
                    isCancelled
                      ? "bg-danger"
                      : lead.lead_status
                      ? "bg-secondary"
                      : "bg-primary"
                  }`}
                >
                  {lead.lead_status ?? "New"}
                </span>
              </h4>
              <div className="d-flex gap-2">
                {/* PRINT */}
                <button
                  type="button"
                  className="btn btn-success btn-sm"
                  onClick={onClickPrint}
                  disabled={!lead || isPrinting}
                  title="Print this lead"
                >
                  {isPrinting ? "Preparing…" : "🖨️ Print"}
                </button>

                {/* BACK */}
                <Link
                  to="/business-cod-leads"
                  className="btn btn-outline-secondary btn-sm"
                >
                  ← Back
                </Link>
              </div>
            </div>

            <div className="row g-3">
              {/* Left column: details */}
              <div className="col-lg-8">
                {/* Business */}
                <div className="card shadow-sm mb-3">
                  <div className="card-header py-2">
                    <strong>Business</strong>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-4">
                        <small className="text-muted d-block">Name</small>
                        {business.name || "-"}
                      </div>
                      <div className="col-md-4">
                        <small className="text-muted d-block">Phone</small>
                        {business.phone || "-"}
                      </div>
                      <div className="col-md-4">
                        <small className="text-muted d-block">Email</small>
                        {business.email || "-"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Consignee */}
                <div className="card shadow-sm mb-3">
                  <div className="card-header py-2">
                    <strong>Consignee</strong>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-4">
                        <small className="text-muted d-block">Name</small>
                        {consignee.name || "-"}
                      </div>
                      <div className="col-md-4">
                        <small className="text-muted d-block">City</small>
                        {consignee.city || "-"}
                      </div>
                      <div className="col-md-4">
                        <small className="text-muted d-block">Email</small>
                        {consignee.email || "-"}
                      </div>
                      <div className="col-md-4 mt-2">
                        <small className="text-muted d-block">Contact 1</small>
                        {consignee.contact1 || "-"}
                      </div>
                      <div className="col-md-4 mt-2">
                        <small className="text-muted d-block">Contact 2</small>
                        {consignee.contact2 || "-"}
                      </div>
                      <div className="col-md-4 mt-2">
                        <small
                          className="text-muted d-block"
                          style={{ color: "red" }}
                        >
                          Drop Off Address
                        </small>
                        {consignee.full_address || "-"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Product */}
                <div className="card shadow-sm mb-3">
                  <div className="card-header py-2">
                    <strong>Product</strong>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-4">
                        <small className="text-muted d-block">#Order ID</small>
                        {lead.order_id || "-"}
                      </div>
                      <div className="col-md-4">
                        <small className="text-muted d-block">
                          Description
                        </small>
                        {lead.product_desc || "-"}
                      </div>
                      <div className="col-md-4 mt-2">
                        <small className="text-muted d-block">
                          Service Type
                        </small>
                        {lead.service_type || "-"}
                      </div>
                      <div className="col-md-4 mt-2">
                        <small className="text-muted d-block">Qty</small>
                        {lead.item_qty ?? "-"} x
                      </div>
                      <div className="col-md-4 mt-2">
                        <small className="text-muted d-block">Weight</small>
                        {lead.weight ?? "-"} kg
                      </div>
                    </div>
                  </div>
                </div>

                {/* Pricing */}
                <div className="card shadow-sm mb-3">
                  <div className="card-header py-2">
                    <strong>Pricing</strong>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-4">
                        <small className="text-muted d-block">COD Amount</small>
                        {formatCurrency(lead.cod_amount)}
                      </div>
                      <div className="col-md-4">
                        <small className="text-muted d-block">Tax Amount</small>
                        {formatCurrency(lead.tax_amount)}
                      </div>
                      <div className="col-md-4">
                        <small className="text-muted d-block">
                          Total Collect
                        </small>
                        {formatCurrency(lead.total_collect)}
                      </div>
                      <div className="col-md-4 mt-2">
                        <small className="text-muted d-block">Created</small>
                        {formatDateTime(lead.lead_created_at)}
                      </div>
                      <div className="col-md-4 mt-2">
                        <small className="text-muted d-block">Updated</small>
                        {formatDateTime(lead.lead_updated_at)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Rate Card Implementation */}
                {showMatchingCard ? (
                  // —— EXISTING MATCHING CARD (unchanged) ——
                  <div className="card shadow-sm mb-3">
                    <div
                      className="card-header py-2 d-flex align-items-center justify-content-between"
                      style={{ backgroundColor: "#D4DBF9" }}
                    >
                      <strong>
                        Rate Card Match &amp; Quote{" "}
                        <Link to={`/businesses/${originalBusinessId}/rates`}>
                          <i
                            className="bx bx-edit"
                            id="rate"
                            style={{ color: "red" }}
                          ></i>
                        </Link>
                        <UncontrolledTooltip placement="right" target="rate">
                          Edit Rate Card?
                        </UncontrolledTooltip>
                      </strong>
                      <span className="text-muted small">
                        {zone.replace("_", " ")}
                      </span>
                    </div>

                    <div
                      className="card-body"
                      style={{ border: "1px solid #D4DBF9" }}
                    >
                      {quoteLoading && <div>Calculating…</div>}
                      {!quoteLoading && quoteError && (
                        <div className="alert alert-warning mb-0">
                          {quoteError}
                        </div>
                      )}
                      {!quoteLoading && !quoteError && quote && (
                        <>
                          {/* —— keep your existing Inputs / Matched Rules / Breakdown —— */}
                          {/* Inputs */}
                          <div className="row mb-3">
                            <div className="col-md-3">
                              <small className="text-muted d-block">
                                Service
                              </small>
                              <div className="fw-semibold">
                                {quote.inputs.service_type}
                              </div>
                            </div>
                            <div className="col-md-3">
                              <small className="text-muted d-block">
                                Weight
                              </small>
                              <div className="fw-semibold">
                                {quote.inputs.weight} kg
                              </div>
                            </div>
                            <div className="col-md-3">
                              <small className="text-muted d-block">
                                COD Amount
                              </small>
                              <div className="fw-semibold">
                                {formatCurrency(quote.inputs.cod_amount)}
                              </div>
                            </div>
                            <div className="col-md-3">
                              <small className="text-muted d-block">Zone</small>
                              <div className="fw-semibold">
                                {quote.inputs.zone.replace("_", " ")}
                              </div>
                            </div>
                          </div>

                          {/* Matched Rules */}
                          {/* (keep your existing matched cards exactly as before) */}

                          {/* Breakdown */}
                          <div className="border rounded-3 p-3 mt-3">
                            <div className="row">
                              <div className="col-md-3">
                                <small className="text-muted d-block">
                                  Base (Weight)
                                </small>
                                <div className="fw-semibold">
                                  {formatCurrency(
                                    quote.breakdown.base_weight_charge
                                  )}
                                </div>
                              </div>
                              <div className="col-md-3">
                                <small className="text-muted d-block">
                                  Cash Handling
                                </small>
                                <div className="fw-semibold">
                                  {formatCurrency(
                                    quote.breakdown.cash_handling
                                  )}
                                </div>
                              </div>
                              <div className="col-md-3">
                                <small className="text-muted d-block">
                                  FSC
                                </small>
                                <div className="fw-semibold">
                                  {formatCurrency(quote.breakdown.fsc_amount)}
                                </div>
                              </div>
                              <div className="col-md-3">
                                <small className="text-muted d-block">
                                  GST
                                </small>
                                <div className="fw-semibold">
                                  {formatCurrency(quote.breakdown.gst_amount)}
                                </div>
                              </div>
                            </div>
                            <hr className="my-2" />
                            <div className="d-flex justify-content-between">
                              <div className="text-muted">Total</div>
                              <div className="fw-bold">
                                {formatCurrency(quote.breakdown.total)}
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ) : null}

                {showStoredCard ? (
                  // —— NEW: STORED CHARGES (DB) CARD ——
                  <div className="card shadow-sm mb-3">
                    <div className="card-header py-2 d-flex align-items-center justify-content-between">
                      <strong>Final Charges (from DB)</strong>
                      <span className="text-muted small">
                        {zone.replace("_", " ")}
                      </span>
                    </div>
                    <div className="card-body">
                      {!charges ? (
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="text-muted">
                            Stored charges not loaded yet or unavailable.
                          </div>
                          <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => fetchCharges(resolvedLeadId)}
                          >
                            Refresh
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="row">
                            <div className="col-md-3">
                              <small className="text-muted d-block">
                                Base (Weight)
                              </small>
                              <div className="fw-semibold">
                                {formatCurrency(charges.base_weight_charge)}
                              </div>
                            </div>
                            <div className="col-md-3">
                              <small className="text-muted d-block">
                                Cash Handling
                              </small>
                              <div className="fw-semibold">
                                {formatCurrency(charges.cash_handling)}
                              </div>
                            </div>
                            <div className="col-md-3">
                              <small className="text-muted d-block">FSC</small>
                              <div className="fw-semibold">
                                {formatCurrency(charges.fsc_amount)}
                              </div>
                            </div>
                            <div className="col-md-3">
                              <small className="text-muted d-block">GST</small>
                              <div className="fw-semibold">
                                {formatCurrency(charges.gst_amount)}
                              </div>
                            </div>
                          </div>
                          <hr className="my-2" />
                          <div className="d-flex justify-content-between">
                            <div className="text-muted">Total</div>
                            <div className="fw-bold">
                              {formatCurrency(charges.total)}
                            </div>
                          </div>

                          {/* Optional: quick link to edit rate card */}
                          <div className="mt-3">
                            <Link
                              to={`/businesses/${originalBusinessId}/rates`}
                              className="btn btn-outline-primary btn-sm"
                              title="Edit Rate Card"
                            >
                              Edit Rate Card
                            </Link>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ) : null}

                {/* History */}
                <div className="card shadow-sm mb-3">
                  <div className="card-header py-2 d-flex align-items-center justify-content-between">
                    <strong>Lead History</strong>
                    <small className="text-muted">
                      {Array.isArray(lead.history)
                        ? `${lead.history.length} entr${
                            lead.history.length === 1 ? "y" : "ies"
                          }`
                        : "0 entries"}
                    </small>
                  </div>
                  <div className="card-body p-0">
                    <div className="table-responsive">
                      <table className="table table-sm mb-0">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: 180 }}>When</th>
                            <th style={{ width: 140 }}>Status</th>
                            <th>Remark</th>
                            <th style={{ width: 220 }}>Actor</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Array.isArray(lead.history) &&
                          lead.history.length > 0 ? (
                            lead.history.map(row => (
                              <tr key={row.id}>
                                <td>{formatDateTime(row.createdAt)}</td>
                                <td>
                                  <span
                                    className={`badge ${
                                      (row.status || "")
                                        .toLowerCase()
                                        .includes("cancel")
                                        ? "bg-danger"
                                        : "bg-secondary"
                                    }`}
                                  >
                                    {row.status || "-"}
                                  </span>
                                </td>
                                <td>{row.remark || "-"}</td>
                                <td>
                                  {row.actor?.name ? row.actor.name : "-"}
                                  {row.actor?.email ? (
                                    <div className="text-muted small">
                                      {row.actor.email}
                                    </div>
                                  ) : null}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td
                                colSpan={4}
                                className="text-center text-muted py-3"
                              >
                                No history yet.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right column: actions */}
              <div className="col-lg-4">
                {/* Creator (optional quick card) */}
                <div className="card shadow-sm mb-3">
                  <div className="card-header py-2">
                    <strong>Created By</strong>
                  </div>
                  <div className="card-body">
                    <div className="mt-2">
                      <small className="text-muted d-block">Name</small>
                      {creator.name || "-"}
                    </div>
                    <div className="mt-2">
                      <small className="text-muted d-block">
                        Pick Up Address
                      </small>
                      {lead.origin || "-"}
                    </div>
                    <div className="mt-2">
                      <small className="text-muted d-block">Phone</small>
                      {creator.phone || "-"}
                    </div>
                    <div className="mt-2">
                      <small className="text-muted d-block">Email</small>
                      {creator.email || "-"}
                    </div>
                  </div>
                </div>
                <div className="card shadow-sm mb-3">
                  <div className="card-header py-2">
                    <strong>Zone</strong>
                  </div>
                  <div className="card-body">
                    {/* Addresses preview (bordered card) */}
                    <div className="mb-3">
                      <div className="p-3 border rounded-3">
                        <div className="row g-3 align-items-start">
                          <div className="col-md-6">
                            <div className="text-muted small mb-1">
                              Origin (Pick-up)
                            </div>
                            <div className="fw-semibold">{originAddress}</div>
                          </div>
                          <div className="col-md-6">
                            <div className="text-muted small mb-1">
                              Drop-off (Full Address)
                            </div>
                            <div className="fw-semibold">
                              {DropCity} / {dropAddress}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Zone selection */}
                    <div className="mb-2">
                      <label className="form-label">Zone Definition</label>
                      <select
                        className={`form-select ${
                          !zone ? "glow-warning" : ""
                        } `}
                        value={zone}
                        onChange={e => setZone(e.target.value)}
                        disabled={
                          isCancelled || (isPersistedLocked && !editMode)
                        }
                      >
                        <option value="">-- Select zone --</option>
                        <option value="within_city">Within City</option>
                        <option value="same_zone">Same Zone</option>
                        <option value="different_zone">Different Zone</option>
                      </select>
                    </div>

                    {/* <button
                      className="btn btn-primary w-100"
                      onClick={saveZone}
                      disabled={isCancelled || !zone}
                    >
                      Save Zone
                    </button> */}
                  </div>
                </div>

                <div className="card shadow-sm mb-3">
                  <div className="card-header py-2">
                    <strong>Assign Rider</strong>
                  </div>
                  <div className="card-body">
                    <div className="mb-2">
                      <label className="form-label">Rider</label>
                      <select
                        className={`form-select ${
                          !selectedRider ? "glow-warning" : ""
                        }`}
                        value={selectedRider}
                        onChange={e => setSelectedRider(e.target.value)}
                        disabled={
                          isCancelled || (isPersistedLocked && !editMode)
                        }
                      >
                        <option value="">-- Select rider --</option>
                        {riderOptions.map(r => (
                          <option key={r.value} value={r.value}>
                            {r.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    {/* <button
                      className="btn btn-secondary w-100"
                      onClick={assignRider}
                      disabled={isCancelled || !selectedRider}
                    >
                      Assign
                    </button> */}
                  </div>
                </div>
                <div className="d-grid gap-2">
                  {isPersistedLocked && !isCancelled ? (
                    <div className="d-flex gap-2">
                      {!editMode ? (
                        <button
                          className="btn btn-outline-secondary flex-fill"
                          onClick={() => setEditMode(true)}
                        >
                          Edit
                        </button>
                      ) : (
                        <>
                          <button
                            className="btn btn-outline-secondary flex-fill"
                            onClick={() => {
                              // revert UI values to current lead values
                              setZone(lead.zone ?? "")
                              setSelectedRider(lead.assigned_rider_id ?? "")
                              setEditMode(false)
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            className="btn btn-success flex-fill"
                            onClick={submitRequist}
                            disabled={
                              !selectedRider || !zone || !quote || !isDirty
                            }
                          >
                            Update Request
                          </button>
                        </>
                      )}
                    </div>
                  ) : (
                    <button
                      className="btn btn-success"
                      onClick={submitRequist}
                      disabled={
                        isCancelled || !selectedRider || !zone || !quote
                      }
                    >
                      Submit Request
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div>
              <button
                type="button"
                className="btn btn-success btn-sm"
                onClick={onClickPrint}
                disabled={!lead || isPrinting}
                title="Print this lead"
              >
                {isPrinting ? "Preparing…" : "🖨️ Print"}
              </button>
            </div>
          </div>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default ProcessLeadPage
